/* Public Scan2Call campaign demo — `/demo`
   ------------------------------------------
   Video-player experience. Plays `/scan2call-demo.mp4` (user drops the
   file into `/app/frontend/public/` when they have it) and shows the
   sign-up CTAs below. If the video file isn't present yet, the page
   gracefully falls back to a "Demo video coming soon" placeholder with
   the same CTAs still visible — so the demo page never breaks even
   before the user records their walkthrough.

   ══════════════════════════════════════════════════════════════════
   TO REPLACE THE DEMO VIDEO
     1. Record a short (30-90 s) MP4 walkthrough of a Scan2Call ring
        — visitor scans QR, ringing, resident answers, deterrent,
        hangup. H.264 encoded, 720p or 1080p, under 20 MB is ideal.
     2. Save the file as `scan2call-demo.mp4` in
        `/app/frontend/public/` — it will be served at `/scan2call-
        demo.mp4` via the public asset pipeline.
     3. (Optional) Add a poster frame image at
        `/app/frontend/public/scan2call-demo-poster.jpg` for a nicer
        pre-play thumbnail.
     4. Redeploy. No code change needed.
   ══════════════════════════════════════════════════════════════════
*/
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import axios from "axios";
import { Bell, Play, X, Share2, ExternalLink, RotateCcw, ShieldCheck, Copy, Check, Sparkles, VideoOff, Upload, Loader2, VolumeX } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { uploadDemoVideo, captureVideoThumbnail, uploadDemoThumbnail, VIDEO_MAX_BYTES } from "../lib/uploadDemoVideo";
import BrandingShowcase from "../components/doorbell/BrandingShowcase";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GUARD_MARKETING_URL =
  "https://www.contritrack.com/?utm_source=scan2call_demo&utm_medium=demo_end&utm_campaign=viral_share";

// Public asset paths — user drops the MP4 here later. See file header
// for the drop-in instructions.
const DEMO_VIDEO_SRC = "/scan2call-demo.mp4";
const DEMO_POSTER_SRC = "/scan2call-demo-poster.jpg";

export default function DoorbellDemo() {
  const [showShareOverlay, setShowShareOverlay] = useState(false);
  const [copied, setCopied] = useState(false);
  const [refCode, setRefCode] = useState(null);
  // `videoStatus`:
  //   "loading" — src set, waiting for canplay
  //   "playing" — video element reported play() success
  //   "ended"   — video finished naturally → auto-show outro CTAs
  //   "missing" — src errored (usually 404 until the user uploads the
  //               MP4) → show the placeholder pitch card
  const [videoStatus, setVideoStatus] = useState("loading");
  // Video source resolution: an admin-uploaded video (served from the
  // backend, survives redeploys) beats the static /public asset.
  const [videoSrc, setVideoSrc] = useState(DEMO_VIDEO_SRC);
  const [posterSrc, setPosterSrc] = useState(DEMO_POSTER_SRC);
  // System admins get a "Change campaign video" button on this page.
  const [isSysAdmin, setIsSysAdmin] = useState(false);
  const [uploadPct, setUploadPct] = useState(null); // null = idle
  // True when the browser blocked autoplay-with-sound and we fell back
  // to muted playback — surfaces a "Tap for sound" pill.
  const [needsUnmute, setNeedsUnmute] = useState(false);

  const videoRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Both are best-effort: anonymous visitors 401 on /auth/me and see
    // no admin button; missing backend video falls back to the static
    // public asset.
    (async () => {
      try {
        const r = await axios.get(`${API}/doorbell/demo-video/meta`);
        if (r.data?.available) {
          setVideoSrc(`${API}/doorbell/demo-video?v=${encodeURIComponent(r.data.updated_at || "")}`);
        }
        if (r.data?.thumbnail_available) {
          setPosterSrc(`${API}/doorbell/demo-video/thumbnail?v=${encodeURIComponent(r.data.thumbnail_updated_at || "")}`);
        }
      } catch { /* noop */ }
    })();
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setIsSysAdmin(r.data?.role === "system_admin");
      } catch { /* anonymous visitor */ }
    })();
  }, []);

  const handleFileSelected = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    if (!(file.type || "").startsWith("video/")) {
      toast.error("Please select a video file (MP4 recommended).");
      return;
    }
    if (file.size > VIDEO_MAX_BYTES) {
      toast.error(`Video too large — keep it under ${Math.round(VIDEO_MAX_BYTES / (1024 * 1024))}MB.`);
      return;
    }
    setUploadPct(0);
    try {
      await uploadDemoVideo(file, setUploadPct);
      toast.success("Campaign video replaced — now playing the new one.");
      setVideoStatus("loading");
      setVideoSrc(`${API}/doorbell/demo-video?v=${Date.now()}`);
      // Best-effort: frame-grab a 1200×630 thumbnail in-browser and store
      // it as the poster + the site's og:image for link unfurls.
      try {
        const thumbB64 = await captureVideoThumbnail(file);
        await uploadDemoThumbnail(thumbB64);
        setPosterSrc(`${API}/doorbell/demo-video/thumbnail?v=${Date.now()}`);
        toast.success("Share thumbnail refreshed from the new video.");
      } catch { /* thumbnail is optional — OG falls back to app icon */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed — please try again.");
    } finally {
      setUploadPct(null);
    }
  }, []);

  // Referral code from `?ref=` — same behaviour as before.
  useEffect(() => {
    try {
      const raw = new URLSearchParams(window.location.search).get("ref") || "";
      const cleaned = raw.replace(/[^A-Za-z0-9]/g, "").slice(0, 32);
      if (cleaned) {
        localStorage.setItem("guard.ref", cleaned);
        setRefCode(cleaned);
      } else {
        const stored = localStorage.getItem("guard.ref");
        if (stored) setRefCode(stored);
      }
    } catch { /* private mode — no referral tracking */ }
  }, []);

  const demoUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/demo`;
  }, []);

  const handleReplay = useCallback(() => {
    if (!videoRef.current) return;
    try {
      videoRef.current.currentTime = 0;
      // Replay is a user gesture — sound is always allowed here.
      videoRef.current.muted = false;
      setNeedsUnmute(false);
      videoRef.current.play?.().catch(() => { /* noop */ });
      setVideoStatus("playing");
    } catch { /* noop */ }
  }, []);

  // Audio-on by default. Browsers only allow unmuted autoplay in some
  // cases (user has interacted with the site before, high media
  // engagement, etc.) — so we TRY sound-on first and gracefully fall
  // back to muted playback + a "Tap for sound" pill when blocked.
  const handleLoadedData = useCallback(() => {
    setVideoStatus("playing");
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    v.volume = 1;
    const p = v.play();
    if (p?.catch) {
      p.catch(() => {
        v.muted = true;
        setNeedsUnmute(true);
        v.play().catch(() => { /* noop — user can hit the controls */ });
      });
    }
  }, []);

  const handleUnmute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    v.volume = 1;
    setNeedsUnmute(false);
    v.play().catch(() => { /* noop */ });
  }, []);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(demoUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — long-press the link to share.");
    }
  }, [demoUrl]);

  const handleNativeShare = useCallback(async () => {
    if (!navigator.share) {
      handleCopyLink();
      return;
    }
    try {
      await navigator.share({
        title: "Guard Scan2Call — see the demo",
        text: "Turn any door into a video-call intercom. Watch this 60-second demo:",
        url: demoUrl,
      });
    } catch { /* user cancelled */ }
  }, [demoUrl, handleCopyLink]);

  const downloadQr = useCallback(() => {
    const canvas = document.querySelector('canvas[data-testid="demo-qr-canvas"]');
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = "guard-scan2call-demo-qr.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("QR downloaded — print & share!");
  }, []);

  // ---- render --------------------------------------------------------

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="demo-page">
      {/* Header — brand + share button */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
        <div className="inline-flex items-center gap-1.5">
          <Bell className="w-4 h-4 text-emerald-400" />
          <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-300 font-body">
            Guard · Scan2Call Demo
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isSysAdmin && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                className="hidden"
                onChange={handleFileSelected}
                data-testid="demo-video-file-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadPct !== null}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200 text-[11px] font-body font-bold uppercase tracking-widest transition-colors disabled:opacity-70"
                data-testid="demo-change-video-btn"
              >
                {uploadPct !== null ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span data-testid="demo-upload-progress">{uploadPct}%</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-3.5 h-3.5" /> Change video
                  </>
                )}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => setShowShareOverlay(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 text-white text-[11px] font-body font-bold uppercase tracking-widest transition-colors"
            data-testid="demo-share-btn"
          >
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </div>
      </header>

      {/* Video stage */}
      <div className="flex-1 w-full flex items-center justify-center px-4 py-4">
        <div className="w-full max-w-2xl">
          {refCode && (
            <div
              className="bg-emerald-500/10 border border-emerald-400/40 rounded-sm p-3 mb-4"
              data-testid="demo-referral-nudge"
            >
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-emerald-400" />
                <p className="text-xs text-emerald-100 font-body">
                  <span className="font-bold">You&apos;ve been invited!</span> Sign up
                  and get an extra <span className="font-bold">₦100</span> free
                  credit — courtesy of the friend who sent you here.
                </p>
              </div>
            </div>
          )}

          {videoStatus === "missing" ? (
            /* Fallback — no MP4 yet. Show a nice pitch card so the page
               still converts even before the user records the demo. */
            <div
              className="aspect-video rounded-md bg-gradient-to-br from-emerald-900/40 via-black to-black border border-emerald-400/20 flex items-center justify-center p-6 relative overflow-hidden"
              data-testid="demo-video-placeholder"
            >
              <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_30%_30%,rgba(52,211,153,0.3),transparent_50%)]" />
              <div className="relative text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500 mb-4">
                  <VideoOff className="w-7 h-7 text-emerald-400" />
                </div>
                <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-2">
                  Demo video coming soon
                </h1>
                <p className="text-sm text-gray-300 font-body max-w-md mx-auto">
                  Meanwhile — Guard Scan2Call turns any gate into a video-call
                  intercom. Visitors scan your QR and reach you as if calling on
                  WhatsApp. See a visitor before you answer, scare off unwanted
                  ones with a fierce dog bark or police siren, no app install
                  needed on their side.
                </p>
              </div>
            </div>
          ) : (
            <div className="relative aspect-video rounded-md bg-black overflow-hidden shadow-2xl">
              <video
                key={videoSrc}
                ref={videoRef}
                src={videoSrc}
                poster={posterSrc}
                autoPlay
                controls
                playsInline
                preload="metadata"
                onLoadedData={handleLoadedData}
                onEnded={() => setVideoStatus("ended")}
                onError={() => setVideoStatus("missing")}
                className="w-full h-full object-cover bg-black"
                data-testid="demo-video"
              >
                <track kind="captions" />
              </video>
              {needsUnmute && videoStatus === "playing" && (
                <button
                  type="button"
                  onClick={handleUnmute}
                  className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-body font-bold uppercase tracking-widest shadow-lg transition-colors"
                  data-testid="demo-unmute-btn"
                >
                  <VolumeX className="w-4 h-4" /> Tap for sound
                </button>
              )}
              {videoStatus === "ended" && (
                <div
                  className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center"
                  data-testid="demo-video-ended-overlay"
                >
                  <button
                    type="button"
                    onClick={handleReplay}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-body font-bold uppercase tracking-widest text-xs transition-colors shadow-lg"
                    data-testid="demo-replay"
                  >
                    <RotateCcw className="w-4 h-4" /> Watch again
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Outro CTAs — always visible, not gated behind video end.
              Highest-intent audience is watching this page; keep the
              sign-up path always one tap away. */}
          <div
            className="mt-5 bg-gray-950 border border-emerald-400/30 rounded-md p-4 sm:p-5"
            data-testid="demo-cta-panel"
          >
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-300 font-body">
                Get free wallet credit
              </p>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter mb-3">
              Ready to turn your gate into a Scan2Call?
            </h2>
            <p className="text-xs text-gray-400 font-body mb-4">
              Sign up in 30 seconds. No credit card. Free wallet credit to try
              the first few calls. Print your QR, stick it on your gate.
            </p>
            <div className="flex flex-col gap-2">
              <a
                href={GUARD_MARKETING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 h-12 px-4 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-body font-bold uppercase tracking-widest transition-colors"
                data-testid="demo-signup-cta"
              >
                Get your own Guard <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <button
                type="button"
                onClick={() => setShowShareOverlay(true)}
                className="inline-flex items-center justify-center gap-1 h-11 px-3 rounded-sm border border-emerald-400/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 text-xs font-body font-bold uppercase tracking-widest transition-colors"
                data-testid="demo-share-cta"
              >
                <Share2 className="w-3.5 h-3.5" /> Share this demo
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4 text-[10px] text-gray-500 font-body">
              <div className="flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span>No card</span>
              </div>
              <div className="flex items-center gap-1">
                <Play className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span>30 s setup</span>
              </div>
              <div className="flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span>Free credit</span>
              </div>
            </div>
          </div>

          {/* Corporate branding upsell — sample branded QR posters */}
          <BrandingShowcase qrValue={demoUrl || "https://www.contritrack.com/demo"} />
        </div>
      </div>

      {/* Share overlay */}
      {showShareOverlay && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
          data-testid="demo-share-overlay"
          onClick={() => setShowShareOverlay(false)}
        >
          <div
            className="bg-gray-950 border border-emerald-400/30 rounded-lg p-5 w-full max-w-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-400 font-body">
                  Campaign QR
                </p>
                <h3 className="font-heading text-xl font-black tracking-tighter">
                  Share the demo
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowShareOverlay(false)}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                data-testid="demo-share-close"
                aria-label="Close share"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-gray-400 font-body mb-4">
              Print, post, or share this QR. Anyone who scans it lands on
              this demo — no signup needed.
            </p>

            <div
              className="bg-white p-4 rounded-md flex items-center justify-center mb-4"
              data-testid="demo-qr-container"
            >
              <QRCodeCanvas
                value={demoUrl}
                size={220}
                level="H"
                includeMargin={false}
                bgColor="#ffffff"
                fgColor="#000000"
                imageSettings={{
                  src: "/icons/icon-192.png",
                  width: 46,
                  height: 46,
                  excavate: true,
                }}
                data-testid="demo-qr-canvas"
              />
            </div>

            <div className="flex items-center justify-between bg-black border border-white/10 rounded-sm px-2.5 py-2 mb-3">
              <span className="text-[11px] font-mono text-emerald-300 truncate mr-2" data-testid="demo-share-url">
                {demoUrl}
              </span>
              <button
                type="button"
                onClick={handleCopyLink}
                className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-body font-bold text-emerald-400 hover:text-emerald-300"
                data-testid="demo-copy-link"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={downloadQr}
                className="h-11 bg-emerald-500 hover:bg-emerald-600 text-black font-body font-bold rounded-sm"
                data-testid="demo-download-qr"
              >
                Download PNG
              </Button>
              <Button
                onClick={handleNativeShare}
                variant="outline"
                className="h-11 border-emerald-400/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 rounded-sm font-body font-bold"
                data-testid="demo-native-share"
              >
                <Share2 className="w-4 h-4 mr-1.5" /> Share…
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
